import React, { useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { Box, Typography, Skeleton } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";

import Content from "../../../components/Content";
import { GetCustomer } from "../../../hooks/useCustomerData";
import { useIsSuperAdmin } from "../../../hooks/useIsSuperAdmin";
import { OrgGroupsTab } from "./OrgGroupsTab";
import { OrgUsersTab } from "./OrgUsersTab";
import { OrgFacilitiesTab } from "./OrgFacilitiesTab";
import { OrgSettingsTab } from "./OrgSettingsTab";
import { INK, INK_MUTED, LINE, BRAND } from "./orgTabsKit";

const TABS = [
  { id: "groups", label: "Grupper" },
  { id: "users", label: "Användare" },
  { id: "facilities", label: "Anläggningar" },
  { id: "settings", label: "Inställningar" },
];

const TabStrip = ({ active, onChange }) => (
  <Box
    sx={{
      display: "flex",
      gap: 0.5,
      mb: 4,
      borderBottom: `1px solid ${LINE}`,
      flexWrap: "wrap",
    }}
  >
    {TABS.map((tab) => {
      const isActive = tab.id === active;
      return (
        <Box
          key={tab.id}
          component="button"
          type="button"
          onClick={() => onChange(tab.id)}
          sx={{
            position: "relative",
            px: 2.25,
            py: 1.5,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "14px",
            fontWeight: 600,
            color: isActive ? INK : INK_MUTED,
            transition: "color 0.2s ease",
            "&:hover": { color: INK },
            "&::after": {
              content: '""',
              position: "absolute",
              left: 0,
              right: 0,
              bottom: "-1px",
              height: "2px",
              background: BRAND,
              transform: isActive ? "scaleX(1)" : "scaleX(0)",
              transition: "transform 0.25s cubic-bezier(0.22,1,0.36,1)",
            },
          }}
        >
          {tab.label}
        </Box>
      );
    })}
  </Box>
);

export const OrganisationDetailsView = () => {
  const { id } = useParams();
  const organisationId = Number(id);
  const navigate = useNavigate();
  const { isSuperAdmin, loading: authLoading } = useIsSuperAdmin();
  const [tab, setTab] = useState("groups");

  const { data: customerData, isLoading, isError } = GetCustomer(id);
  const organisation = customerData?.data;

  if (authLoading) return null;
  if (!isSuperAdmin) return <Navigate to="/admin/overview" replace />;

  const orgName = organisation?.organisation_name || "Organisation";

  return (
    <Content>
      <Box sx={{ px: { xs: 1, md: 2 }, pt: 2, pb: 6, minHeight: "100%" }}>
        <Box
          component="button"
          type="button"
          onClick={() => navigate("/admin/organisations")}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            mb: 3,
            px: 1.5,
            py: 0.75,
            background: "transparent",
            border: "none",
            color: INK_MUTED,
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            transition: "color 0.2s ease",
            "&:hover": { color: BRAND },
          }}
        >
          <ArrowBackRoundedIcon sx={{ fontSize: 16 }} />
          Alla organisationer
        </Box>

        {isLoading ? (
          <Skeleton width="40%" height={56} />
        ) : isError || !organisation ? (
          <Box
            sx={{
              py: 8,
              textAlign: "center",
              border: `1px dashed ${LINE}`,
              borderRadius: "16px",
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Outfit", sans-serif',
                fontSize: "1.5rem",
                fontWeight: 600,
                color: INK,
              }}
            >
              Organisationen hittades inte
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 4 }}>
              <Typography
                sx={{
                  fontFamily: '"Outfit", sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: "2.25rem", md: "3rem" },
                  lineHeight: 1,
                  letterSpacing: "-0.04em",
                  color: INK,
                  mb: 1.5,
                }}
              >
                {orgName}
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  color: INK_MUTED,
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                <PlaceRoundedIcon sx={{ fontSize: 16 }} />
                {organisation.municipality || "Ingen kommun angiven"}
              </Box>
            </Box>

            <TabStrip active={tab} onChange={setTab} />

            {tab === "groups" && (
              <OrgGroupsTab organisationId={organisationId} orgName={orgName} />
            )}
            {tab === "users" && (
              <OrgUsersTab organisationId={organisationId} orgName={orgName} />
            )}
            {tab === "facilities" && (
              <OrgFacilitiesTab
                organisationId={organisationId}
                orgName={orgName}
              />
            )}
            {tab === "settings" && (
              <OrgSettingsTab organisationId={organisationId} />
            )}
          </>
        )}
      </Box>
    </Content>
  );
};

export default OrganisationDetailsView;
